/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

const constants = require('@utils/_constants');

/**
 * @module enervee_utils.rebates
 */
RebatesService.$inject = [
    '$q',
    '$log',
    '$http',
    '$filter',
    '$timeout',
    '$rootScope',
    'AccountAuthService',
    'AnalyticsService',
    'CmsContentService',
    'GetFetchService',
    'IndexService',
    'RebateApiConsumerService',
    'SignupService',
    'TemplateService',
    'UtilitySelectorCookieService',
    'UtilService',
    'Upload',
    'WITH_CREDENTIALS',
];
function RebatesService(
    $q,
    $log,
    $http,
    $filter,
    $timeout,
    $rootScope,
    AccountAuthService,
    AnalyticsService,
    CmsContentService,
    GetFetchService,
    IndexService,
    RebateApiConsumerService,
    SignupService,
    TemplateService,
    UtilitySelectorCookieService,
    UtilService,
    Upload,
    WITH_CREDENTIALS
) {
    var _pub = {},
        _utilityId = null;

    _pub.utilityChangedCallbacks = [];

    /**
     * Checks to see if the site is using the Easy Rebate process
     */
    _pub.allowEasyRebate = function () {
        return UtilService.isFeatureEnabled('easy_rebate_claim');
    };

    /**
     * Checks to see if the site is using the Email rebate process
     */
    _pub.allowEmailRebate = function () {
        return UtilService.isFeatureEnabled('email_rebate_claim');
    };

    /**
     * Pulls site setting configured to show/hide image on the rebate modal
     */
    _pub.showRebateImage = function () {
        return UtilService.isFeatureEnabled('show_image_on_rebate_modal');
    };

    _pub.rebatesEnabled = function () {
        return UtilService.isFeatureEnabled('rebate_submission');
    };

    /**
     * Accepts an object with the user's email, a receipt image, the rebate id, and the product id. Also accepts
     * a boolean variable to check if the call originated in a modal. Checks to ensure that all data is valid
     * and that a user is logged in. If the user is not logged in, prompts for login. Otherwise, returns any
     * errors or an empty object to signify that the submission can continue.
     * @param data
     * @param is_modal
     * @returns {*}
     */
    _pub.verifyRebateInfo = function(data, is_modal) {
        if (!AccountAuthService.isAuthenticated()) {
            IndexService.setSignupModalHeaders();
            IndexService.openModal('#modal-enter-email');
            if (is_modal) {
                AccountAuthService.onAuthChange(function(){
                    IndexService.openModal('#rebate-processing');
                });
                return null;
            }
        } else if (data.receipt_image === null) {
            return {'error': 'Please select a receipt image.'};
        } else if (data.receipt_image.type.split('/')[0] !== 'image') {
            return {'error': 'Invalid File Type. File must be an image.'};
        } else {
            return {};
        }
    };

    /**
     * Accepts an email address, a product id, a rebate id, and the id of the modal it was called from,
     * if it was called from a modal. If the user was logged in, trigger the backend to create the claim
     * (sendEmailInfo) and return the result. If the user was not logged in, register sendRebateInfoCallback
     * with the AccountAuthService, go through a log in process for the user, then return the result.
     * @param email
     * @param productId
     * @param rebateId
     * @param modalId
     * @returns boolean
     */
    _pub.verifyEmailInfo = function(email, productId, rebateId, modalId) {
        var deferred = $q.defer();
        // Placed in a function in case we need to add it to the AccountAuthService callback list
        var sendRebateInfoCallback = function() {
            if (AccountAuthService.isAuthenticated()) {
                if (modalId) {
                    IndexService.openModal(modalId);
                }
                var data = {
                    'email': email,
                    'product_id': productId,
                    'rebate_id': rebateId
                };
                return _pub.sendEmailInfo(data).then(function(data) {
                    var alerts = [{
                        type: 'success',
                        message: data.message
                    }];
                    IndexService.setHeaderAlerts(alerts);
                    if (modalId) {
                        IndexService.closeModal(modalId);
                    }
                    return data;
                });
            }
        };

    // Logged in already, just call sendRebateInfoCallback, save the promise, and return true to designate
    // that the promise is ready.
        if (AccountAuthService.isAuthenticated()) {
            return sendRebateInfoCallback();
        }

        // Not logged in, lets check if the email exits
        SignupService.doesUserEmailExist(email).then(function () {
            var _user = SignupService.getDoesUserExist();
            $rootScope.user = {email: email};

            // Register the callback to AccountAuthService
            AccountAuthService.onAuthChange(function () {
                deferred.resolve(sendRebateInfoCallback());
            });
            if (_user.exists === true && _user.password_set === true) {
                // Load the enter password modal
                if (modalId) {
                    IndexService.switchModal(modalId, '#modal-enter-password');
                } else {
                    IndexService.openModal('#modal-enter-password');
                }
                SignupService.signInPasswordModalOpenedCallBack("Rebate Claim Opt In");
            } else {
                // Load the create a password modal
                AccountAuthService.setIsAuthenticated(true);
                SignupService.signUpModalOpenedCallBack(constants.EVENT_REBATE_CLAIM_OPT_IN);
                if (modalId) {
                    IndexService.switchModal(modalId, '#modal-create-password');
                } else {
                    IndexService.openModal('#modal-create-password');
                }
            }
        });
        return deferred.promise;
    };

    /**
     * Accepts an object with the current user's email, the rebate id, and the product id. Also accepts a boolean
     * variable to check if the call originated in a modal. Verifies that a user is logged in. If there is not
     * an authenticated user, this will prompt for login. Returns an empty object to signify that the submission
     * can continue.
     * @param data
     * @param isModal
     * @returns {*}
     */
    _pub.emailRebateInfo = function(data, isModal) {
        if (!AccountAuthService.isAuthenticated()){
            IndexService.setSignupModalHeaders();
            IndexService.openModal('#modal-enter-email');
            if (isModal) {
                AccountAuthService.onAuthChange(function(){
                    IndexService.openModal('#rebate-processing');
                });
            }
            return null;
        } else {
            return {};
        }
    };

    _pub.sendEmailInfo = function(data) {
        var deferred = $q.defer();
        RebateApiConsumerService.post('api/1.0/claim/email_rebate_info/', data, WITH_CREDENTIALS).then(function (response) {
            deferred.resolve(response.data);
        }, function (response) {
            deferred.resolve(response.data);
        });

        return deferred.promise;
    };

    /**
     * Accepts a validated data object to submit to the backend. Handles both receipt uploads and email more info
     * requests. Returns the backend's response on upload completion.
     * @param data
     * @returns {Promise}
     */
    _pub.uploadInfo = function(data) {
        var deferred = $q.defer();
        var rebate = {};
        rebate.upload = Upload.upload({
            url: RebateApiConsumerService.getFullUrl('/api/1.0/claim/new_rebate_upload/'),
            data: data
        });
        rebate.upload.then(function (response) {
            $timeout(function () {
                deferred.resolve(response);
            });
        }, function (response) {
            deferred.resolve(response);
        });

        return deferred.promise;
    };

    /**
     * Checks if rebates should be shown based on if the user has chosen a utility
     * provider or not.
     * @returns {*}
     */
    _pub.checkShowRebates = function() {
        _utilityId = UtilitySelectorCookieService.getUtilityId();
        return _pub.rebatesEnabled();
    };

    /**
     * Listens for the event that is broadcasted when a user chooses a utility provider.
     * Checks to see if we should be displaying rebates, and calls all registered events.
     */
    $rootScope.$on('utilitySet', function(){
        _pub.utilityChangedCallbacks.forEach(function (callback) {
            callback();
        });
    });

    /**
     * Adds events to run in the case that the user changes their chosen utility.
     * @param event
     */
    _pub.onUtilityChanged = function(event) {
        _pub.utilityChangedCallbacks.push(event);
    };

    /**
     * Helper function for making the key for rebate requests
     * @param {String|Array} productIds list of product ids to get rebates for
     * @returns {String}
     */
    var getRebateRequestKey = function(productIds) {
        if (Array.isArray(productIds)) {
            return `${productIds.join(',')}-${_utilityId}`;
        }
        return `${productIds}-${_utilityId}`;
    };

    /**
     * For each product check for rebates of the same type
     * and add parameter for marking rebates with highest amount
     */
    function markHighestAmountRebates(productRebates) {
        const rebateByType = {}
        // get rebate for each type with highest amount, exclude combo
        productRebates.forEach(rebate => {
            rebate.highestInType = false;
            if (rebate.business_rule_codes !== undefined && rebate.business_rule_codes.length == 1) {
                const type = rebate.business_rule_codes[0] || "";
                if (!(type in rebateByType)) {
                    rebateByType[type] = rebate;
                } else if (parseInt(rebateByType[type].amount) < parseInt(rebate.amount)) {
                    rebateByType[type] = rebate;
                }
            }
        });
        for (const type in rebateByType) {
            rebateByType[type].highestInType = true;
        }
    }

    /*
     * Filter out additional rebates
     * @param {Array} rebates list of all rebates
    */
    _pub.getHighestRebates = (rebates) => {
        return rebates.filter( (rebate) => rebate.highestInType)
    };

    /*
     * Get sum of rebates for claim rebate tab (excludes "DR" and additional discounts)
     * @param {Array} rebates list of all rebates
    */
    _pub.getSumForClaimTab = (rebates) => {
        return rebates
            .filter(rebate =>
                rebate.highestInType && isPastPurchaseRebate(rebate)
            )
            .reduce((sum, rebate) => sum + Number(rebate.amount), 0);
    };

    /*
     * Get rebate with the highest rebate amount (excludes DR)
     * @param {Array} rebates list of all rebates
    */
    _pub.getHighestRebateForClaimTab = (rebates) => {
        let highestRebate;
        rebates.forEach(rebate => {
            if ((!highestRebate || parseInt(highestRebate.amount) < parseInt(rebate.amount))
                && isPastPurchaseRebate(rebate)) {
                highestRebate = rebate;
            }
        });
        return highestRebate;
    };

    /*
     * Returns whether a rebate is claimable for a past purchase
     * @param {Object} rebate object
    */
    function isPastPurchaseRebate(rebate) {
        return rebate.business_rule_codes[0] !== constants.DEMAND_RESPONSE_BUSINESS_CODE;
    };

    /**
     * Takes a list of productIds and queries the backend for rebates associated with
     * the products that belong to the user's chosen provider.
     * @param {String|Array} productIds list of product ids to get rebates for
     * @param {Boolean} [force] if true, re-fetch the rebates even if
     * they've already been fetched once
     * @returns {$q.promise}
     */
    _pub.getRebatesForProducts = function(productIds, force) {
        var deferred = $q.defer();
        var chunkedProductIds = UtilService.chunkArray(productIds);
        var rebatePromises = [];
        var fetchedRebates = {};
        _utilityId = UtilitySelectorCookieService.getUtilityId();
        chunkedProductIds.forEach(function (ids) {
            var key = getRebateRequestKey(ids);
            var urlOptions = {
                url: RebateApiConsumerService.getFullUrl('/api/2.0/rebates/rebates/'),
                params: {
                    provider_id: _utilityId,
                    product_ids: ids
                }
            };
            var promise = GetFetchService.get(['rebates', key], urlOptions, {force: force}).then(function (data) {
                if (typeof data !== 'object') {
                    GetFetchService.clear(['rebates', key])
                    urlOptions.url = RebateApiConsumerService.getFullUrl('/api/1.0/rebates/rebates/');
                    return GetFetchService.get(['rebates', key], urlOptions, {force: force}).then(function (data) {
                        for (const key in data) {
                            data[key] = [data[key]];
                        }
                        return data;
                    });
                } else {
                    return data
                }
            }).then(function (data) {
                UtilService.objectValues(data).forEach(rebateArray => markHighestAmountRebates(rebateArray));
                angular.merge(fetchedRebates, data);
            });
            rebatePromises.push(promise);
        });
        $q.all(rebatePromises).then(function () {
            deferred.resolve(fetchedRebates);
        });
        return deferred.promise;
    };

    /**
     * Takes a list of productIds and queries the backend for rebate lites associated with
     * the products that belong to the user's chosen provider.
     * @param {String|Array} productIds list of product ids to get rebate lites for
     * @param {Boolean} [force] if true, re-fetch the rebates even if
     * they've already been fetched once
     * @returns {$q.promise}
     */
    _pub.getRebateLitesForProducts = function(productIds, force) {
        var deferred = $q.defer();
        var chunkedProductIds = UtilService.chunkArray(productIds);
        var rebatePromises = [];
        var fetchedRebates = {};
        _utilityId = UtilitySelectorCookieService.getUtilityId();
        chunkedProductIds.forEach(function (ids) {
            var key = getRebateRequestKey(ids);
            var urlOptions = {
                url: RebateApiConsumerService.getFullUrl('/api/1.0/rebates/rebate-lites/'),
                params: {
                    provider_id: _utilityId,
                    product_ids: ids
                }
            };
            var promise = GetFetchService.get(['rebate-lites', key], urlOptions, {force: force}).then(function (data) {
                angular.merge(fetchedRebates, data);
            });
            rebatePromises.push(promise);
        });
        $q.all(rebatePromises).then(function () {
            deferred.resolve(fetchedRebates);
        });
        return deferred.promise;
    };

    /**
     * Submits the rebate claim form. Success or errors are passed along appropriately.
     * @param data
     * @returns {*}
     */
    _pub.submitRebateClaim = function (data) {
        var deferred = $q.defer();
        RebateApiConsumerService.post(
            'api/1.0/claim/new-claim-form/',
            data
        ).then(function(data){
            deferred.resolve(data.data);
        }, function(error){
            deferred.reject(error.data);
        });
        return deferred.promise;
    };

    /**
     * Checks rebate claim validity on form access.
     * @param claimId
     * @returns {*}
     */
    _pub.checkRebateValidity = function(claimId) {
        var deferred = $q.defer();
        RebateApiConsumerService.post(
            'api/1.0/claim/check-status/',
            {'rebate_claim_id': claimId},
            WITH_CREDENTIALS
        ).then(function(data){
            deferred.resolve(data);
        }, function(error){
            deferred.reject(error.data);
        });
        return deferred.promise;
    };

    /**
     * Takes a category_id and queries the CMS backend, as specified in SETTINGS, for form text and
     * any custom fields.
     *
     * @param categoryId
     * @returns {*}
     */
    _pub.getCMSdata = function(categoryId) {
        var deferred = $q.defer();
        $http.get(
            UtilService.getSiteSetting('cms_base_url') + 'claims/claim-form/?category_id=' + categoryId
        ).then(function(data){
            deferred.resolve(data.data);
        }, function(error){
            deferred.reject(error);
        });
        return deferred.promise;
    };

    /**
     * Handles tracking when an incentive is clicked.
     *
     * @param {Object} incentive - the rebate or rebate lite to track
     * @param {Object} product - the product associated with the rebate
     * @param {String} location - where the incentive is on the page
     */
    _pub.trackIncentiveClicked = function (incentive, product, location) {
        location = location || 'mainContent';
        var trackingData = {
            incentive_amount: incentive.amount,
            incentive_id: incentive.id,
            location: location,
            link_to_claim_rebate: incentive.url || incentive.link || '',
        };
        return AnalyticsService.reportEvent('Incentive Clicked', trackingData, product);
    };

    /**
     * Handles tracking when a user engages with an incentive or completes/fails an incentive application.
     *
     * @param {String} status - what type of engagment. can be Engaged, Completed, or Failed
     * @param {Object} trackingData - the data to be tracked in segment
     * @param {Object} products - product(s) to be tracked
     */
    _pub.trackIncentiveEngaged = function (status, trackingData, products) {
        products = UtilService.forceArray(products);
        var eventName = 'Incentive ' + status;
        trackingData.categories = [];
        products.forEach(function (product) {
            if (trackingData.categories.indexOf(product.category_name) === -1) {
                trackingData.categories.push(product.category_name);
            }
        });
        return AnalyticsService.reportEvent(eventName, trackingData, products);
    };

    /**
     * Takes a product and returns rebate tag text accordingly. Will return no rebate text if
     * the category is marked as such, will return kit text if it exists, or just rebate text from
     * CMS. Returns null if no rebate exists and category is not marked to return no rebate text.
     * @param product
     */
    _pub.getRebateTagText = function (product) {
        const rebates = product.rebates || [];
        const incentives = product.incentives || [];

        if (!rebates.length && !incentives.length) {
            return CmsContentService.getMarketPlaceText('product_card:no_rebate_text').then(function (text) {
                return text;
            });
        }
        if (incentives.length > 0) {
            return getIncentivesTagText(product);
        } else if (rebates.length > 0) {
            // get amount and text for multiple products or single product
            if (rebates.length > 1) {
                return getTextMultipleRebate(product);
            } else {
                return getTextSingleRebate(product);
            }
        } else {
            return $q.resolve(null);
        }
    };

    /**
     * Will return no rebate text if
     * the category is marked as such, will return kit text if it exists, or just rebate text from
     * CMS. Returns null if no rebate exists and category is not marked to return no rebate text.
     * @param rebate
     */
    function getTextSingleRebate(product) {
        const rebate = product.rebates[0];

        const rebateAmount = checkForAdditionalDiscounts(product, rebate.amount);

        const rebateAmountText = $filter('toCurrency')(rebateAmount);
        if (rebate.marketplace_tag_text) {
            const tagText = TemplateService.render(
                rebate.marketplace_tag_text,
                {'amount': rebateAmountText}
            );
            return $q.resolve(tagText);
        }
        if (rebate.kit) {
            return CmsContentService.getMarketPlaceText(
                'product_card:incentive_addon',
                {
                    'rebate_amount': rebateAmountText
                }
            );
        } else if (product.checkoutInfo && product.checkoutInfo.total_additional_discounts) {
            return CmsContentService.getMarketPlaceText(
                'product_card:rebate_discount_tag_text',
                {
                    'rebate_amount': rebateAmountText
                },
                product.category.id
             );
        } else {
            return CmsContentService.getMarketPlaceText(
                'product_card:rebate_tag_text',
                {
                    'rebate_amount': rebateAmountText
                }
            );
        }
    }

   /**
     * Add if additional discounts available for product
     * @param product
     * @returns text to display on card for rebates
     */
   function checkForAdditionalDiscounts(product, totalRebatesAmount) {
        if (product.checkoutInfo && product.checkoutInfo.total_additional_discounts) {
            totalRebatesAmount = parseFloat(totalRebatesAmount) + parseFloat(product.checkoutInfo.total_additional_discounts);
        }

        //set total amount on product for other uses
        product.totalRebatesAmount = totalRebatesAmount;
        return totalRebatesAmount;
   }

   /**
     * Calculate total amount of all rebates available for product
     * @param product
     * @returns total amount
     */
   _pub.getMultipleRebatesAmount = (product) => {
        const rebates = _pub.getHighestRebates(product.rebates || []);
        let totalRebatesAmount = rebates.reduce((acc, rebate) => acc + parseFloat(rebate.amount), 0.0);
        totalRebatesAmount = checkForAdditionalDiscounts(product, totalRebatesAmount);
        return totalRebatesAmount;
   };

   /**
     * Calculate total amount of all rebates available for product
     * @param product
     * @returns text to display on card for rebates
     */
    function getTextMultipleRebate(product) {
        const totalRebatesAmount = _pub.getMultipleRebatesAmount(product);

        const rebateAmountText = $filter('toCurrency')(totalRebatesAmount);
        return CmsContentService.getMarketPlaceText(
            'product_card:multiple_rebates_tag_text',
            {
                'rebate_amount': rebateAmountText
            },
            product.category.id
        );
    }

    function getIncentivesTagText (product) {
        const incentives = product.incentives;
        if (!angular.isArray(incentives)) {
            return $q.resolve(null);
        }
        const rebate = (incentives.length > 0 && incentives[0].isUtilityRebate) ? incentives[0] : null;
        if (rebate !== null && rebate.kit) {
            return CmsContentService.getMarketPlaceText('product_card:incentive_addon').then(function(text) {
                if (product.totalIncentivesAmount > 0) {
                    // product has both monetary rebate and rebate add-on
                    // rebate amount is in a different place, so we don't need to add it to tag text
                    return text;
                } else if (product.totalIncentivesAmount === 0) {
                    // product only has incentive add-on
                    return text;
                }
            });
        } else if ((rebate !== null && !rebate.kit) || rebate === null && incentives.length > 0) {
            return CmsContentService.getMarketPlaceText('product_card:rebate_tag_text').then(function(text){
                return $filter('toCurrency')(product.totalIncentivesAmount) + text;
            });
        } else {
            return $q.resolve(null);
        }
    }

    /**
     * Gets product rebates from backend and adds any rebate lites to the list of incentives.
     * @param products
     * @returns {*}
     */
    _pub.getProductIncentives = function (products) {
        if (!Array.isArray(products)) {
            products = [products];
        }
        var incentivesObj = {};

        var rebatesPromises = [];
        var requestedRebates = {};
        var requestedRebateLites = {};
        var productIds = UtilService.reduceToProperty(products, 'id');
        var promise = _pub.getRebatesForProducts(productIds).then(function (data) {
            requestedRebates = data;
        }, function (error) {
            $log.warn(error);
        });
        rebatesPromises.push(promise);
        var promise2 = _pub.getRebateLitesForProducts(productIds).then(function (data) {
            requestedRebateLites = data;
        }, function (error) {
            $log.warn(error);
        });
        rebatesPromises.push(promise2);
        return $q.all(rebatesPromises).then(function () {
            products.forEach(function(product){
                var incentivesTotalAmount = 0;
                incentivesObj[product.id] = {incentives: []};
                if (product.id in requestedRebateLites) {
                    incentivesObj[product.id].incentives = UtilService.forceArray(requestedRebateLites[product.id]);
                }
                if (product.id in requestedRebates) {
                    requestedRebates[product.id].isUtilityRebate = true;
                    const rebates = requestedRebates[product.id] || [];
                    incentivesObj[product.id].incentives = rebates.concat(incentivesObj[product.id].incentives);
                }
                incentivesObj[product.id].incentives.forEach(function (incentive) {
                    incentivesTotalAmount += parseFloat(incentive.amount) || 0;
                });
                incentivesObj[product.id].totalAmount = incentivesTotalAmount;
            });
            return incentivesObj;
        });
    };

    /**
     * Get rebates for  products list and assign them.
     * @param products Array of products
     */
     _pub.getAllRebatesForProducts = function (products) {
        const productIds = products.map((product) => product.id);
        products.forEach(product => { product.rebatesLoading = true;});

        $q.all([
            _pub.getRebatesForProducts(productIds).catch((error) => {$log.warn(error);}),
            _pub.getRebateLitesForProducts(productIds).catch((error) => {$log.warn(error);}),
        ]).then(([rebates, rebateLites]) => {
            products.forEach(product => {
                product.rebates = rebates[product.id] || [];
                product.rebateLite = rebateLites[product.id] || null;
            });
        }).catch((error) => {
            $log.warn(error);
        }).finally(() => {
            products.forEach(product => product.rebatesLoading = false);
        });
    };

    /**
     * Helper function that adds a rebates property to a list of cart items
     * @param {Object[]} cartItems An array of cart item objects
     */
    _pub.addRebatesProperty = function (cartItems) {
        cartItems.forEach(cartItem => {
            cartItem.rebates = [];
            if (cartItem.rebate) {
                cartItem.rebates.push(cartItem.rebate);
            }
        });
    };

    /**
     * Helper function to format filters
     * @param {String} filters
     * @param {String} categoryCode
     * @returns {Object[]} of formated for swiftype filters
     */
    function formatFilters(filters, categoryCode) {
            const pairs = filters.split('&');
            let results = {}
            results[categoryCode] = {};
            pairs.forEach( pair => {
                pair = pair.split('=')
                if (pair.length === 2) {
                    results[categoryCode][pair[0]] = [pair[1].replace(/-/g, ' ')];
                }
            });
            return results;
    }

    /**
     * Get maximum rebates for category.
     * @param {String} categoryCode
     * @param {String} filters
     * @returns maximum available rebate amount
     */
    _pub.getMaxRebatesForCategory = function (categoryCode, filters) {
        const _utilityId = UtilitySelectorCookieService.getUtilityId();
        const urlOptions = {
            url: RebateApiConsumerService.getFullUrl('/api/2.0/rebates/rebate-max/'),
            method: 'POST',
            data: {
                utility_id: _utilityId,
                document_types: [categoryCode]
            }
        };
        if (filters) {
            urlOptions.data.filters = formatFilters(filters, categoryCode);
        }
        const key = categoryCode + filters;
        return GetFetchService.get(['rebate-max', key], urlOptions);
    };

    return _pub;
}

module.exports = RebatesService;
